import { create } from 'zustand';
import { salesApi } from '../services/salesApi';
import type {
  SalesLead,
  SalesComment,
  SalesLeadExample,
  SalesCompanyProfile,
  SalesStatusHistoryEntry,
} from '../types/sales';

interface SalesState {
  leads: SalesLead[];
  leadsTotal: number;
  leadsPage: number;
  leadsLoading: boolean;

  archivedLeads: SalesLead[];
  archivedTotal: number;
  archivedPage: number;
  archivedLoading: boolean;

  selectedLead: SalesLead | null;
  selectedLeadLoading: boolean;

  comments: SalesComment[];
  commentsLoading: boolean;

  history: SalesStatusHistoryEntry[];
  historyLoading: boolean;

  examples: SalesLeadExample[];
  examplesLoading: boolean;

  profile: SalesCompanyProfile | null;
  profileLoading: boolean;

  modalOpen: boolean;
  modalLeadId: string | null;
  focusComment: boolean;
}

interface SalesActions {
  fetchLeads: (params?: { archived?: boolean; page?: number; per_page?: number }) => Promise<void>;
  fetchArchivedLeads: (page?: number) => Promise<void>;
  fetchLead: (id: string) => Promise<void>;
  updateLead: (
    id: string,
    data: {
      status?: string;
      sales_rating?: number;
      pipeline_order?: number;
      contact_phone?: string | null;
    },
  ) => Promise<SalesLead | null>;
  toggleArchive: (id: string) => Promise<void>;

  fetchComments: (leadId: string) => Promise<void>;
  createComment: (leadId: string, content: string, replyToId?: string) => Promise<void>;

  fetchHistory: (leadId: string) => Promise<void>;

  fetchExamples: () => Promise<void>;
  createExample: (data: { type: 'good' | 'bad'; company_url: string; explanation: string }) => Promise<void>;
  deleteExample: (id: string) => Promise<void>;

  fetchProfile: () => Promise<void>;
  updateProfile: (description: string) => Promise<void>;

  openLeadModal: (leadId: string, focusComment?: boolean) => void;
  closeLeadModal: () => void;

  optimisticStatusUpdate: (leadId: string, newStatus: string) => void;
  optimisticKanbanUpdate: (leadId: string, patch: { status?: string; pipeline_order?: number }) => void;
}

const _inflight = new Map<string, boolean>();

export const useSalesStore = create<SalesState & SalesActions>((set, get) => ({
  leads: [],
  leadsTotal: 0,
  leadsPage: 1,
  leadsLoading: false,

  archivedLeads: [],
  archivedTotal: 0,
  archivedPage: 1,
  archivedLoading: false,

  selectedLead: null,
  selectedLeadLoading: false,

  comments: [],
  commentsLoading: false,

  history: [],
  historyLoading: false,

  examples: [],
  examplesLoading: false,

  profile: null,
  profileLoading: false,

  modalOpen: false,
  modalLeadId: null,
  focusComment: false,

  fetchLeads: async (params) => {
    const key = `leads-${JSON.stringify(params)}`;
    if (_inflight.get(key)) return;
    _inflight.set(key, true);
    set({ leadsLoading: true });
    try {
      const data = await salesApi.listLeads({ archived: false, per_page: 200, ...params });
      set({ leads: data.leads, leadsTotal: data.total, leadsPage: data.page });
    } finally {
      set({ leadsLoading: false });
      _inflight.delete(key);
    }
  },

  fetchArchivedLeads: async (page = 1) => {
    if (_inflight.get('archived')) return;
    _inflight.set('archived', true);
    set({ archivedLoading: true });
    try {
      const data = await salesApi.listArchivedLeads(page);
      set({ archivedLeads: data.leads, archivedTotal: data.total, archivedPage: data.page });
    } finally {
      set({ archivedLoading: false });
      _inflight.delete('archived');
    }
  },

  fetchLead: async (id) => {
    set({ selectedLeadLoading: true });
    try {
      const lead = await salesApi.getLead(id);
      set({ selectedLead: lead });
    } finally {
      set({ selectedLeadLoading: false });
    }
  },

  updateLead: async (id, data) => {
    try {
      const lead = await salesApi.updateLead(id, data);
      set((s) => ({
        leads: s.leads.map((l) => (l.id === id ? lead : l)),
        selectedLead: s.selectedLead?.id === id ? lead : s.selectedLead,
      }));
      return lead;
    } catch {
      return null;
    }
  },

  toggleArchive: async (id) => {
    const lead = await salesApi.toggleArchive(id);
    set((s) => ({
      leads: lead.archived ? s.leads.filter((l) => l.id !== id) : s.leads,
      archivedLeads: !lead.archived
        ? s.archivedLeads.filter((l) => l.id !== id)
        : s.archivedLeads,
      selectedLead: s.selectedLead?.id === id ? lead : s.selectedLead,
    }));
  },

  fetchComments: async (leadId) => {
    set({ commentsLoading: true });
    try {
      const comments = await salesApi.listComments(leadId);
      set({ comments });
    } finally {
      set({ commentsLoading: false });
    }
  },

  createComment: async (leadId, content, replyToId) => {
    await salesApi.createComment(leadId, content, replyToId);
    await get().fetchComments(leadId);
  },

  fetchHistory: async (leadId) => {
    set({ historyLoading: true });
    try {
      const history = await salesApi.getHistory(leadId);
      set({ history });
    } finally {
      set({ historyLoading: false });
    }
  },

  fetchExamples: async () => {
    set({ examplesLoading: true });
    try {
      const examples = await salesApi.listExamples();
      set({ examples });
    } finally {
      set({ examplesLoading: false });
    }
  },

  createExample: async (data) => {
    await salesApi.createExample(data);
    await get().fetchExamples();
  },

  deleteExample: async (id) => {
    await salesApi.deleteExample(id);
    set((s) => ({ examples: s.examples.filter((e) => e.id !== id) }));
  },

  fetchProfile: async () => {
    set({ profileLoading: true });
    try {
      const profile = await salesApi.getProfile();
      set({ profile });
    } finally {
      set({ profileLoading: false });
    }
  },

  updateProfile: async (description) => {
    const profile = await salesApi.updateProfile(description);
    set({ profile });
  },

  openLeadModal: (leadId, focusComment = false) => {
    set({ modalOpen: true, modalLeadId: leadId, focusComment });
    const url = new URL(window.location.href);
    url.searchParams.set('lead', leadId);
    if (focusComment) url.searchParams.set('focus', 'comment');
    window.history.replaceState({}, '', url.toString());
  },

  closeLeadModal: () => {
    set({ modalOpen: false, modalLeadId: null, focusComment: false, selectedLead: null, comments: [], history: [] });
    const url = new URL(window.location.href);
    url.searchParams.delete('lead');
    url.searchParams.delete('focus');
    window.history.replaceState({}, '', url.toString());
  },

  optimisticStatusUpdate: (leadId, newStatus) => {
    set((s) => ({
      leads: s.leads.map((l) => (l.id === leadId ? { ...l, status: newStatus as any } : l)),
    }));
  },

  optimisticKanbanUpdate: (leadId, patch) => {
    set((s) => ({
      leads: s.leads.map((l) => {
        if (l.id !== leadId) return l;
        const next = { ...l };
        if (patch.status !== undefined) next.status = patch.status as SalesLead['status'];
        if (patch.pipeline_order !== undefined) next.pipeline_order = patch.pipeline_order;
        return next;
      }),
    }));
  },
}));
